/* eslint no-param-reassign: 0 */

import {Trans} from '@lingui/macro';
import produce from 'immer';
import React, {useCallback, useMemo} from 'react';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {OrNull} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {CustomFieldResourceTypesEnum, CustomFieldsEnum} from '../../../models/api/customFieldModel';
import {CommonSchema} from '../../../models/api/integrationTypesModel';
import {useBaseEditorContext} from '../../editors/baseEditorContext';
import {KnownSchemaRefsEnum, Parameter} from '../../editors/commons/parameterModel';
import {isStringTemplate} from '../../editors/commons/stringTemplateModel';
import {isClientAtom} from '../../editors/expressions/expressionAtomModel';
import {filterOutPreviousExpressions} from '../../editors/expressions/expressionInferHelpers';
import {Expression} from '../../editors/expressions/expressionModel';
import {ExpressionRef} from '../../editors/expressions/expressionRefModel';
import {getCompatibleInferredTypesWithManifest} from '../../editors/expressions/expressionResultHelpers';
import {useExpressionsContext} from '../../editors/expressions/expressionsContext';
import {RuleDefaultCardTypesEnum} from '../ruleActionConditionHelpers';
import {RuleAction, ruleActionsAccountGroups, RuleActionTypesEnum} from '../ruleActionModel';
import {RuleCondition, RuleConditionTypesEnum} from '../ruleConditionModel';
import {RuleActionId, RuleConditionId} from '../ruleParameterModel';
import {currentTimezone} from '../ruleTimeHelpers';
import {SelectedDropdownItem} from './dropdowns/dropdownWithExpressions';
import {useOnSelectCreateExpressionRef} from './hooks/useOnSelectCreateExpression';
import {InputBoolean} from './inputs/inputBoolean';
import {InputDelayHHMM} from './inputs/InputDelayHHMM';
import {InputNumber} from './inputs/inputNumber';
import {InputStringTemplateParameter} from './inputs/inputStringTemplateParameter';
import {InputTimeRange} from './inputs/inputTimeRange';
import {AnswerParameter} from './parameters/answerParameter';
import {ChannelParameter, ChannelsParameter} from './parameters/channelParameter';
import {CustomFieldParameter} from './parameters/customFieldParameter';
import {DaysParameter} from './parameters/daysParameter';
import {EnumParameter} from './parameters/enumParameter';
import {ExpressionParameter, ExpressionsParameter} from './parameters/expressionParameter';
import {FileExtensionParameter} from './parameters/fileExtensionParameter';
import {GroupParameter} from './parameters/groupParameter';
import {InboxesParameter, InboxParameter} from './parameters/inboxParameter';
import {SnoozePresetParameter} from './parameters/snoozePresetParameter';
import {TagParameter, TagsParameter} from './parameters/tagParameter';
import {TeammateParameter, TeammatesParameter} from './parameters/teammateParameter';
import {TimezoneModel, TimezoneParameter} from './parameters/timezoneParameter';

const {
  isArray,
  isChannel,
  isCustomField,
  isInbox,
  isEnum,
  isGroup,
  isNumber,
  isResponse,
  isExpression,
  isString,
  isSchemaRef,
  isTag,
  isTeammate,
  isTimezone,
  isFileExtension,
  isBoolean,
  isSnoozePreset,
} = Parameter;

/*
 * Types.
 */

interface RuleParameterPickerProps<T extends RuleCondition | RuleAction> {
  conditionOrAction: T;
  cardType: RuleDefaultCardTypesEnum;
  parameterName: string;
  parameter: Parameter;
  resolvedParameter: CommonSchema | undefined;
  isDisabled?: boolean;
  addPartiallyCollapsable?: boolean;
  id?: RuleConditionId | RuleActionId;
  customFieldResourceType?: CustomFieldResourceTypesEnum;

  // Methods.
  onUpdate: HandlerOf<T>;
}

/*
 * Component.
 */

export const RuleParameterPicker = <T extends RuleCondition | RuleAction>({
  conditionOrAction,
  id,
  cardType,
  parameter,
  resolvedParameter,
  parameterName,
  onUpdate,
  isDisabled,
  addPartiallyCollapsable = false,
  customFieldResourceType,
}: RuleParameterPickerProps<T>) => {
  const {namespace} = useBaseEditorContext();
  const {expressions: allExpressions} = useExpressionsContext();

  // @RULES TODO Find a way to avoid `any`
  const currentParams: any = conditionOrAction.params;
  const maybeAtom = isClientAtom(conditionOrAction) ? conditionOrAction : undefined;
  const expressions: ReadonlyArray<Expression> = useMemo(() => {
    if (!maybeAtom) {
      return allExpressions;
    }

    return filterOutPreviousExpressions(allExpressions, maybeAtom);
  }, [maybeAtom, allExpressions]);

  const updateParameter = useCallback(
    (newParameterValue: any) =>
      onUpdate(
        produce(conditionOrAction, (draft: any) => {
          if (!draft.params) {
            draft.params = {};
          }

          draft.params[parameterName] = newParameterValue;
        }),
      ),
    [onUpdate, conditionOrAction, parameterName],
  );

  const maybeOnSelectCreateExpression = useOnSelectCreateExpressionRef((expressionRef) => {
    const currentValue = ((conditionOrAction.params || {}) as any)[parameterName];
    updateParameter(addExpressionRef(parameter, currentValue, expressionRef));
  }, getCompatibleInferredTypesWithManifest(parameter));

  const onSelectCreateExpression = maybeAtom ? undefined : maybeOnSelectCreateExpression;

  const currentValue = currentParams[parameterName];

  const mergeParameter = (newParameterValue: any) => updateParameter({...currentValue, ...newParameterValue});

  if (isSchemaRef(parameter) && parameter.$ref === KnownSchemaRefsEnum.TIME_RANGE) {
    if (addPartiallyCollapsable) {
      const selectedTimezone = (currentValue && currentValue.timezone) || currentTimezone();
      const onTimezoneChange = (timezone: OrNull<SelectedDropdownItem<TimezoneModel>>) =>
        timezone && mergeParameter({timezone});
      return (
        <TimezoneParameter
          selectedItem={selectedTimezone}
          onChange={onTimezoneChange}
          isDisabled={isDisabled}
        />
      );
    }

    return <InputTimeRange range={currentValue} setRange={mergeParameter} isDisabled={isDisabled} />;
  }

  if (isSchemaRef(parameter) && parameter.$ref === KnownSchemaRefsEnum.DAYS_OF_THE_WEEK) {
    return <DaysParameter days={currentValue?.days} onDaysChange={updateParameter} isDisabled={isDisabled} />;
  }

  if (isSchemaRef(parameter) && parameter.$ref === KnownSchemaRefsEnum.DELAY) {
    return (
      <InputDelayHHMM
        granularity="days"
        selectedDelay={currentValue}
        onDelayChange={updateParameter}
        isDisabled={isDisabled}
      />
    );
  }

  if (resolvedParameter && isEnum(resolvedParameter)) {
    return (
      <EnumParameter
        enumParameter={resolvedParameter}
        selectedItem={currentValue}
        isDisabled={isDisabled}
        onChange={updateParameter}
      />
    );
  }

  if (isEnum(parameter)) {
    return (
      <EnumParameter
        enumParameter={parameter}
        selectedItem={currentValue}
        isDisabled={isDisabled}
        onChange={updateParameter}
      />
    );
  }

  if (isSnoozePreset(parameter)) {
    return (
      <SnoozePresetParameter
        value={currentValue}
        onSnoozePresetChange={updateParameter}
        isDisabled={isDisabled}
      />
    );
  }

  if (isTimezone(parameter)) {
    return (
      <TimezoneParameter selectedItem={currentValue} onChange={updateParameter} isDisabled={isDisabled} />
    );
  }

  if (isString(parameter)) {
    return (
      <InputStringTemplateParameter
        stringParameter={parameter}
        value={currentValue}
        onValueChange={updateParameter}
        isDisabled={isDisabled}
        expressions={expressions}
        atom={maybeAtom}
        onSelectCreateExpression={onSelectCreateExpression}
      />
    );
  }

  if (isTag(parameter)) {
    return (
      <TagParameter
        selectedItem={currentValue}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={updateParameter}
        onSelectCreateExpression={onSelectCreateExpression}
      />
    );
  }

  if (isArray(parameter) && isFileExtension(parameter.items)) {
    return <FileExtensionParameter extensions={currentValue} onChange={updateParameter} />;
  }

  if (isArray(parameter) && isTag(parameter.items)) {
    return (
      <TagsParameter
        selectedItems={currentValue}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={updateParameter}
        onSelectCreateExpression={onSelectCreateExpression}
      />
    );
  }

  if (isChannel(parameter)) {
    return (
      <ChannelParameter
        selectedItem={currentValue}
        isDisabled={isDisabled}
        onChange={updateParameter}
        namespace={namespace}
      />
    );
  }

  if (isArray(parameter) && isChannel(parameter.items)) {
    return (
      <ChannelsParameter
        selectedItems={currentValue}
        isDisabled={isDisabled}
        onChange={updateParameter}
        namespace={namespace}
        canSelectAnyItem
      />
    );
  }

  if (isGroup(parameter)) {
    return <GroupParameter selectedItem={currentValue} onChange={updateParameter} isDisabled={isDisabled} />;
  }

  if (isTeammate(parameter)) {
    return (
      <TeammateParameter
        selectedItem={currentValue}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={updateParameter}
        onSelectCreateExpression={onSelectCreateExpression}
      />
    );
  }

  if (isArray(parameter) && isTeammate(parameter.items)) {
    return (
      <TeammatesParameter
        selectedItems={currentValue}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={updateParameter}
        selectedItemsSeparator={
          id === RuleActionTypesEnum.NOTIFY_TEAMMATES ? (
            <Trans id="rule-picker-and">and</Trans>
          ) : (
            <Trans id="rule-picker-or">or</Trans>
          )
        }
        canSelectAnyItem={id === RuleConditionTypesEnum.ASSIGNEE_IN || undefined}
        onSelectCreateExpression={onSelectCreateExpression}
      />
    );
  }

  if (isInbox(parameter)) {
    return (
      <InboxParameter
        selectedItem={currentValue}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={updateParameter}
        type={cardType}
        namespace={namespace}
        onSelectCreateExpression={onSelectCreateExpression}
      />
    );
  }

  if (isArray(parameter) && isInbox(parameter.items)) {
    return (
      <InboxesParameter
        selectedItems={currentValue}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={updateParameter}
        type={cardType}
        namespace={namespace}
        canSelectAnyItem
        onSelectCreateExpression={onSelectCreateExpression}
      />
    );
  }

  if (isResponse(parameter)) {
    return (
      <AnswerParameter
        selectedItem={currentValue}
        onChange={updateParameter}
        isDisabled={isDisabled}
        namespace={namespace}
      />
    );
  }

  if (isCustomField(parameter)) {
    const customFieldType = isTeammateOnly(id) ? CustomFieldsEnum.TEAMMATE : undefined;

    const resourceType = isAccountAction(id) ? CustomFieldResourceTypesEnum.ACCOUNT : customFieldResourceType;

    return (
      <CustomFieldParameter
        selectedItem={currentValue}
        onCustomFieldChange={updateParameter}
        isDisabled={isDisabled}
        customFieldResourceType={resourceType}
        atom={maybeAtom}
        customFieldType={customFieldType}
      />
    );
  }

  if (isExpression(parameter)) {
    return (
      <ExpressionParameter
        selectedItem={currentValue}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={updateParameter}
        atom={maybeAtom}
        onSelectCreateExpression={onSelectCreateExpression}
      />
    );
  }

  if (isArray(parameter) && isExpression(parameter.items)) {
    return (
      <ExpressionsParameter
        selectedItems={currentValue}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={updateParameter}
        atom={maybeAtom}
        expressionFormat={parameter.items.expressionFormat}
        onSelectCreateExpression={onSelectCreateExpression}
      />
    );
  }

  if (isBoolean(parameter)) {
    return (
      <InputBoolean
        booleanParameter={parameter}
        currentValue={currentValue}
        onValueChange={updateParameter}
        isDisabled={isDisabled}
      />
    );
  }

  // Number parameter has the lowest priority as it shouldn't override any other model that uses a number input underneath.
  if (isNumber(parameter)) {
    return (
      <InputNumber
        parameter={parameter}
        value={currentValue}
        onValueChange={updateParameter}
        isDisabled={isDisabled}
        expressions={expressions}
        onSelectCreateExpression={onSelectCreateExpression}
      />
    );
  }

  // Fallback for non-implemented actions / conditions.
  return <div>{parameter.id}</div>;
};

/*
 * Helpers.
 */

function isTeammateOnly(id: RuleActionId | RuleConditionId | undefined) {
  return (
    id === RuleActionTypesEnum.ASSIGN_ACCOUNT_FIELD ||
    id === RuleActionTypesEnum.ASSIGN_CONTACT_FIELD ||
    id === RuleActionTypesEnum.NOTIFY_ACCOUNT_FIELD
  );
}

function isAccountAction(id: RuleActionId | RuleConditionId | undefined) {
  return ruleActionsAccountGroups.find((group) => group.items.includes(id as RuleActionTypesEnum));
}

function addExpressionRef(parameter: Parameter, currentValue: any, expressionRef: ExpressionRef) {
  if (isArray(parameter)) {
    return [expressionRef, ...(currentValue ?? [])];
  }

  if (isString(parameter)) {
    if (!currentValue) {
      return {template: [expressionRef]};
    }

    if (isStringTemplate(currentValue)) {
      return {template: [...currentValue.template, expressionRef]};
    }

    return {template: [currentValue, expressionRef]};
  }

  if (isTag(parameter) || isTeammate(parameter) || isInbox(parameter) || isExpression(parameter)) {
    return expressionRef;
  }

  if (isNumber(parameter)) {
    return {template: [expressionRef]};
  }

  return expressionRef;
}
